import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Popup } from 'react-leaflet'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { ArrowIcon } from 'assets/icons'
import {
  Box,
  Center,
  Grid,
  GridItem,
  IconButton,
  Image,
  Link,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { ImagesPng } from 'assets/images'
import { SingleButton2 } from 'common/Buttons/SingleButton'
import { StyledButtonContainer } from 'common/Buttons/SingleButton/styled'
import { LoadingSpinier } from 'common/Loading'
import {
  addFriend,
  authSelectors,
  deleteFriend,
  getNotificationMessage,
  getPosts,
  removeFriendRequest,
  setChosenNotifBlog,
  setGetProfileItemStatus,
  setPosts,
  setFromEngagementPanel,
  setBlogParent,
  setComments,
  setOpenPopupBlogs,
  setOpenFriendRequest,
  usersSelectors,
  setChatSectionPage
} from 'store'
import {
  openConnectedProfileChat,
  setForceCloseChatPanel
} from 'store/chat/slice'
import {
  IS_FRIEND_STATUSES,
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { COLORS } from 'utils/styles'
import {
  hideCounter,
  isAuth,
  translateFromLocalUrl,
  statusMarkerColor
} from 'utils/helpers'
import { AskToSignUpModal2 } from 'components/Content/AskToSignUpModal2'
import { WalletAddress } from 'components/Content/Map/Markers/MarkerPopup/WalletAddress'
import {
  StyledFlexContainer,
  StyledModalContainer,
  StyledUserProfileContainer,
  StyledWordsContent,
  StyledWordsTitle
} from './styled'

const ctaButtonTextObj = {
  connect: 'Connect',
  contact: 'Contact',
  pending: 'Pending',
  delete: 'Delete',
  remove: 'Remove',
  blocked: 'Blocked',
  loading: <LoadingSpinier
    width="13px"
    height="13px"
    marginTop="5px"
  />
}

const friendshipRole = {
  sender: 'sender',
  recipient: 'recipient'
}

export const MarkerPopup = ({
  selectedMarkerData,
  markerPopupCoords,
  handleClosePopup
}) => {
  const dispatch = useDispatch()
  const { signInStatus } = useSelector(authSelectors)
  const {
    activeProfile,
    addFriendStatus,
    deleteFriendStatus,
    getProfileItemStatus,
    openPopupBlogs,
    profileItem,
    removeFriendRequestStatus
  } = useSelector(usersSelectors)

  const quoteRef = useRef(null)
  const overlayClicked = useRef(false)
  const navigate = useNavigate()
  const dataLink = translateFromLocalUrl()
  const [isCopiedLink, setIsCopiedLink] = useState(false)
  const [copyTextWarning, setCopyTextWarning] = useState('Share')
  const [ctaButtonText, setCtaButtonText] = useState(ctaButtonTextObj.connect)
  const [outAnimation, setOutAnimation] = useState(false)
  const [dynamicOpenModal, setDynamicOpenModal] = useState(false)
  const [loadedProfileImage, setLoadedProfileImage] = useState(false)
  const [loadedBannerImage, setLoadedBannerImage] = useState(false)
  const [hoveringChevron, setHoveringChevron] = useState(false)
  const [afterCloseAction, setAfterCloseAction] = useState('')
  const isLoggedIn = signInStatus === SUCCEEDED || isAuth()

  const validDate = dayjs(selectedMarkerData?.last_accessed)
  const iconMarker = statusMarkerColor(validDate)

  const handleCtaClick = () => {
    if (profileItem?.is_friends === IS_FRIEND_STATUSES.accepted) {
      setOutAnimation(true)
      setAfterCloseAction('open_friend_chat')
    } else if (profileItem?.is_friends === IS_FRIEND_STATUSES.no && activeProfile?.id) {
      dispatch(addFriend(profileItem?.id))
    } else if (
      activeProfile?.id
      && (
        profileItem?.is_friends === IS_FRIEND_STATUSES.pending
        || profileItem?.is_friends === IS_FRIEND_STATUSES.denied
      )
    ) {
      if (profileItem?.friendship_role === friendshipRole.sender) {
        dispatch(removeFriendRequest(profileItem?.id))
      } else if (profileItem?.friendship_role === friendshipRole.recipient) {
        setOutAnimation(true)
        dispatch(setGetProfileItemStatus(0))
        dispatch(setOpenFriendRequest(profileItem?.id))
        setAfterCloseAction('open_notif_panel')
      }
    } else {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'You do not have an active profile. Please setup your profile first.'
        })
      )
    }
  }

  // To be hidden until further instructions
  const handleRemoveConnection = () => {
    // todo move this fucntionality for delete
    dispatch(deleteFriend(profileItem?.id))
    // close Chat pahhel if it is opened
    // to prevent user sending messages after unfriending
    dispatch(setForceCloseChatPanel(true))
  }

  const handleShare = () => {
    setIsCopiedLink(true)
    setCopyTextWarning('COPIED')
    navigator.clipboard.writeText(process.env.REACT_APP_FRONTEND_URL + '/?id=' + profileItem?.id)
    dispatch(
      getNotificationMessage({
        type: SUCCEEDED,
        message: 'Copied'
      })
    )
  }

  const handleSignUpModal = () => {
    if (!dynamicOpenModal) {
      setDynamicOpenModal(true)
    }
  }

  const stillLoading = () => removeFriendRequestStatus === PENDING
    || addFriendStatus === PENDING
    || deleteFriendStatus === PENDING

  useEffect(() => {
    if (removeFriendRequestStatus === PENDING) {
      setCtaButtonText(ctaButtonTextObj.loading)
    }
  }, [removeFriendRequestStatus])

  useEffect(() => {
    if (addFriendStatus === PENDING) {
      setCtaButtonText(ctaButtonTextObj.loading)
    }
  }, [addFriendStatus])

  useEffect(() => {
    if (isCopiedLink) {
      const clearSuccess = setTimeout(() => {
        setCopyTextWarning('Share')
        setIsCopiedLink(false)
      }, 1000)

      return () => {
        clearTimeout(clearSuccess)
      }
    }
    return undefined
  }, [isCopiedLink])

  useEffect(() => {
    if (profileItem?.is_friends === IS_FRIEND_STATUSES.accepted) {
      setCtaButtonText(ctaButtonTextObj.contact)
    } else if (profileItem?.is_friends === IS_FRIEND_STATUSES.pending) {
      if (profileItem?.friendship_role === friendshipRole.sender) {
        setCtaButtonText(ctaButtonTextObj.remove)
      } else if (profileItem?.friendship_role === friendshipRole.recipient) {
        setCtaButtonText(ctaButtonTextObj.pending)
      }
    } else if (profileItem?.is_friends === IS_FRIEND_STATUSES.denied) {
      if (profileItem?.friendship_role === friendshipRole.sender) {
        setCtaButtonText(ctaButtonTextObj.remove)
      } else if (profileItem?.friendship_role === friendshipRole.recipient) {
        setCtaButtonText(ctaButtonTextObj.blocked)
      }
    } else if (profileItem?.is_friends === 'no') {
      setCtaButtonText(ctaButtonTextObj.connect)
    }

    if (profileItem !== null) {
      navigate('/?id=' + profileItem?.id)
    }
  }, [profileItem])

  useEffect(() => {
    if (getProfileItemStatus === REJECTED) {
      setOutAnimation(true)
    }
  }, [getProfileItemStatus])

  useEffect(() => {
    hideCounter()
    setOutAnimation(false)
  }, [])

  return (
    <>
      {
        !isLoggedIn
        && selectedMarkerData?.id
        && (
          <Stack
            position="fixed"
            background="transparent"
            top="0"
            left="0"
            width="100%"
            height="100%"
            zIndex={1409}
            cursor="pointer"
            onClick={() => setDynamicOpenModal(true)}
          />
        )
      }

      <Stack
        id="custom-popup-overlay"
        width="100%"
        height="130%"
        zIndex="399"
        position="fixed"
        marginTop="-50px"
        onClick={(e) => {
          e.stopPropagation()

          if (isLoggedIn && !overlayClicked.current) {
            overlayClicked.current = true

            if (openPopupBlogs) {
              handleClosePopup()
            } else {
              setOutAnimation(true)
            }
          }
        }}
      >
        <AskToSignUpModal2
          dynamicOpenModal={dynamicOpenModal}
          setDynamicOpenModal={setDynamicOpenModal}
          profileItem={profileItem}
        />
        <Popup
          maxWidth={5000}
          minWidth={300}
          closeButton={false}
          closeOnClick={false}
          offset={[0, (-window.innerHeight - 850) / 100]}
          position={markerPopupCoords}
          autoClose
        >
          {profileItem === null ? (
            <StyledModalContainer
              style={{
                height: 'auto',
                opacity: outAnimation ? 0 : 1
              }}
            >
              <Box width="100%" height="170px">
                <LoadingSpinier />
              </Box>
            </StyledModalContainer>
          ) : (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                height: 'calc(100dvh - 150px - env(safe-area-inset-top))',
                overflowY: 'clip',
                position: 'relative'
              }}
            >
              <StyledModalContainer
                id="marker-popup-container"
                initial={{ y: outAnimation ? 0 : window.innerHeight }}
                animate={{ y: outAnimation ? window.innerHeight : 0 }}
                transition={{
                  type: 'tween',
                  duration: openPopupBlogs ? 0 : 0.8
                }}
                onAnimationComplete={() => {
                  if (outAnimation) {
                    handleClosePopup()
                    overlayClicked.current = false

                    if (afterCloseAction === 'open_friend_chat') {
                      dispatch(setChatSectionPage('chat-main'))
                      dispatch(openConnectedProfileChat(profileItem))
                    } else if (afterCloseAction === 'open_notif_panel') {
                      document.querySelector('#open-notif-panel').click()
                    }
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  height: 'calc(100dvh - 150px - env(safe-area-inset-top))',
                  position: 'absolute'
                }}
              >
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  direction="row"
                  p="0px 5px"
                  background={COLORS.goldGradientRightToLeft}
                  minHeight="48px"
                  borderRadius="10px 10px 0 0"
                  marginTop="-1px"
                  zIndex={5}
                  position="sticky"
                  top="-0.2"
                  sx={{
                    transform: 'translateZ(0)',
                    willChange: 'transform'
                  }}
                  onClick={() => {
                    if (!isLoggedIn && !dynamicOpenModal) {
                      setDynamicOpenModal(true)
                    }
                  }}
                  cursor={isLoggedIn ? 'auto' : 'pointer'}
                >
                  <IconButton
                    onClick={() => {
                      if (isLoggedIn && !dynamicOpenModal) {
                        document.querySelector('#custom-popup-overlay')?.click()
                      }
                    }}
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'mouse') {
                        setHoveringChevron(true)
                      }
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType === 'mouse') {
                        setHoveringChevron(false)
                      }
                    }}
                    _hover={{ background: COLORS.gold_hover }}
                    background="unset"
                    transform="rotate(270deg)"
                    transition="none"
                    marginLeft="auto"
                    icon={(
                      <>
                        <Text
                          fontWeight="300"
                          transform="rotate(90deg)"
                          display={hoveringChevron ? 'flex' : 'none'}
                        >
                          Exit
                        </Text>

                        <Stack display={hoveringChevron ? 'none' : 'flex'}>
                          <ArrowIcon height="15px" />
                        </Stack>
                      </>
                    )}
                    outline="none"
                    boxShadow="none"
                    css={{
                      ':focus': {
                        outline: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  />
                </Stack>

                <StyledUserProfileContainer>
                  <Skeleton
                    isLoaded={loadedBannerImage}
                    fadeDuration={1}
                    position="absolute"
                    width="100%"
                    height="95px"
                    zIndex={1}
                    gPosition="center"
                    bgRepeat="no-repeat"
                    bgSize="cover"
                    borderTopRadius={10}
                    top={0}
                    padding={0}
                  >
                    <Image
                      src={profileItem?.banner || ImagesPng?.DefaultImage}
                      height="95px"
                      width="100%"
                      onLoad={() => setLoadedBannerImage(true)}
                    />
                  </Skeleton>
                  <Grid
                    width="100%"
                    height="80px"
                    marginX={5}
                    marginY={2}
                    templateRows="repeat(2, 1fr)"
                    templateColumns="repeat(6, 1fr)"
                    zIndex={2}
                    gap={2.5}
                  >
                    <GridItem
                      colSpan={2}
                      rowSpan={2}
                      width="86px"
                      sx={{
                        transform: 'translateZ(0)',
                        WebkitTransform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        willChange: 'transform'
                      }}
                    >
                      <Center>
                        <Skeleton
                          isLoaded={loadedProfileImage}
                          fadeDuration={1}
                          boxSize="75px"
                          objectFit="cover"
                          marginTop="3px"
                          borderRadius="full"
                        >
                          <Image
                            src={profileItem?.profile_picture || ImagesPng.DefaultImage}
                            alt="image"
                            borderRadius="full"
                            border={`1.5px solid ${COLORS.gray_op8}`}
                            onLoad={() => setLoadedProfileImage(true)}
                          />
                        </Skeleton>
                      </Center>
                    </GridItem>
                    <GridItem
                      colSpan={4}
                      rowSpan={1}
                      width="100%"
                      sx={{
                        transform: 'translateZ(0)',
                        WebkitTransform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        willChange: 'transform'
                      }}
                    >
                      {profileItem?.is_my_profile ? null : (
                        <Stack
                          direction="row"
                          position="relative"
                          float="right"
                          marginLeft="0px"
                          marginRight="-10px"
                          sx={{
                            '@media (max-width: 480px)': {
                              marginLeft: profileItem?.is_friends === IS_FRIEND_STATUSES.accepted ? '10px' : '40%'
                            }
                          }}
                        >
                          <Stack direction="row" height="22.5px">
                            <SingleButton2
                              text={copyTextWarning}
                              dataLink={dataLink}
                              click={() => {
                                if (isAuth()) {
                                  handleShare()
                                } else {
                                  handleSignUpModal()
                                }
                              }}
                              name="share"
                              className="cardButton share"
                              customStyled={{
                                minWidth: '55px',
                                fontSize: '12px',
                                paddingTop: window.innerHeight <= 800 ? '3.5px' : '4px',
                                ...(isAuth() ? {} : { backgroundColor: COLORS.semiLightGray })
                              }}
                            />
                            <SingleButton2
                              click={() => {
                                if (isAuth()) {
                                  handleCtaClick()
                                } else {
                                  handleSignUpModal()
                                }
                              }}
                              name="connect-status"
                              text={ctaButtonText}
                              className="cardButton"
                              customStyled={{
                                minWidth: '55px',
                                fontSize: '12px',
                                paddingTop: window.innerHeight <= 800 ? '3.5px' : '4px',
                                pointerEvents: stillLoading() ? 'none' : 'auto',
                                ...(isAuth() ? {} : { backgroundColor: COLORS.semiLightGray })
                              }}
                            />
                            {
                              profileItem?.is_friends === IS_FRIEND_STATUSES.accepted
                                ? (
                                  <SingleButton2
                                    className="cardButton"
                                    customStyled={{
                                      minWidth: '55px',
                                      fontSize: '12px',
                                      paddingTop: window.innerHeight <= 800 ? '3.5px' : '4px',
                                      pointerEvents: stillLoading() ? 'none' : 'auto'
                                    }}
                                    click={handleRemoveConnection}
                                    text={
                                      deleteFriendStatus === PENDING
                                        ? ctaButtonTextObj.loading
                                        : ctaButtonTextObj.delete
                                    }
                                    name="delete-connect"
                                  />
                                )
                                : null
                            }
                          </Stack>
                        </Stack>
                      )}
                    </GridItem>
                    <GridItem
                      rowSpan={1}
                      colSpan={4}
                      width="100%"
                      sx={{
                        transform: 'translateZ(0)',
                        willChange: 'transform'
                      }}
                    >
                      <Stack
                        position="relative"
                        direction="column"
                        spacing="4px"
                      >
                        <Stack
                          alignItems="center"
                          direction="row"
                          spacing="10px"
                        >
                          <Text
                            fontSize="15px"
                            background={COLORS.transparentGray}
                            p="5px"
                            position="absolute"
                            borderRadius={3}
                            width="auto"
                            data-name="profileCardName"
                            sx={{
                              '@media (max-width: 375px)': {
                                'word-break': 'break-all'
                              },
                              '@media (max-width: 390px)': {
                                margin: '-5px 0 0 0 !important'
                              },
                              '@media (max-width: 480px)': {
                                margin: '-5px 0 0 -3% !important'
                              },
                              '@media (max-width: 480px) and (max-height: 800px)': {
                                margin: '-5px 0 0 -6.5% !important'
                              },
                              margin: '-5px 0 0 -7% !important'
                            }}
                          >
                            {profileItem?.full_name}
                          </Text>
                        </Stack>
                      </Stack>
                    </GridItem>
                  </Grid>
                </StyledUserProfileContainer>

                {
                  profileItem?.personal_quote ? (
                    <Stack
                      ref={quoteRef}
                      direction="column"
                      fontSize={15}
                      marginX={5}
                      padding="10px 5px"
                      maxHeight="100px"
                      minHeight={(quoteRef?.current?.scrollHeight > 100 ? 100 : quoteRef?.current?.scrollHeight) + 'px'}
                      overflowY="auto"
                      borderRadius="5px"
                      backgroundImage={COLORS.whiteBlackGradientRightToLeft}
                    >
                      <Text
                        as="span"
                        align="center"
                        whiteSpace="pre-wrap"
                      >
                        {profileItem?.personal_quote}
                      </Text>
                    </Stack>
                  ) : ''
                }

                <Stack
                  textAlign="center"
                  direction="column"
                  gap="1rem"
                  justifyContent="space-between"
                  paddingX="20px"
                  fontSize={15}
                >
                  <Stack
                    justifyContent="space-between"
                    direction="row"
                  >
                    <Text as="span" fontWeight="bold">
                      Connections
                    </Text>
                    <Text as="span">
                      {profileItem?.active_connections}
                    </Text>
                  </Stack>

                  <Stack
                    justifyContent="space-between"
                    direction="row"
                  >
                    <Text as="span" fontWeight="bold">Disconnections</Text>
                    <Text as="span">
                      {profileItem?.past_connections}
                      %
                    </Text>
                  </Stack>

                  <Stack
                    justifyContent="space-between"
                    direction="row"
                  >
                    <Text
                      as="span"
                      fontWeight="bold"
                    >
                      Status
                    </Text>
                    <Text as="span">
                      {iconMarker}
                    </Text>
                  </Stack>
                </Stack>

                <StyledWordsTitle>Affinities</StyledWordsTitle>

                <StyledFlexContainer>
                  {profileItem?.affinities?.map((aff) => (
                    <StyledWordsContent key={aff?.id}>{aff.title}</StyledWordsContent>
                  ))}
                </StyledFlexContainer>

                {profileItem?.social_links ? (
                  <>
                    <StyledWordsTitle>Social Profiles</StyledWordsTitle>

                    <Stack direction="column" marginX={5}>
                      <Stack direction="column">
                        {profileItem.social_links?.length > 0 ? (
                          <Stack direction="row" spacing="10px">
                            <Stack gap="1rem 10px" spacing="unset" flexWrap="wrap" direction="row">
                              {profileItem.social_links.map((sl) => (
                                <Box
                                  key={sl?.id}
                                  borderRadius="full"
                                  bg={isAuth() ? COLORS.goldGradient : COLORS.semiLightGray}
                                  p="6px 10px"
                                >
                                  {
                                    isAuth()
                                      ? (
                                        <Link
                                          href={sl.link}
                                          target="_blank"
                                          name={sl.name}
                                          _cursor="pointer"
                                          style={{
                                            textDecoration: 'none',
                                            color: 'black'
                                          }}
                                        >
                                          {sl.name}
                                        </Link>
                                      )
                                      : (
                                        <Box
                                          cursor="pointer"
                                          onClick={() => handleSignUpModal()}
                                        >
                                          {sl.name}
                                        </Box>
                                      )
                                  }
                                </Box>
                              ))}
                            </Stack>
                          </Stack>
                        ) : (
                          <Text as="span" color={COLORS.gray}>No data available</Text>
                        )}
                      </Stack>
                    </Stack>
                  </>
                ) : null}

                <StyledButtonContainer
                  onClick={() => {
                    if (isAuth()) {
                      if (activeProfile) {
                        dispatch(setPosts([]))
                        dispatch(setComments([]))
                        dispatch(setBlogParent([]))
                        dispatch(setFromEngagementPanel({}))
                        dispatch(setChosenNotifBlog([]))

                        dispatch(getPosts({
                          type: 'marker',
                          id: selectedMarkerData?.id
                        }))

                        dispatch(setOpenPopupBlogs(true))
                      } else {
                        dispatch(
                          getNotificationMessage({
                            type: REJECTED,
                            message: 'You do not have an active profile. Please setup your profile first.'
                          })
                        )
                      }
                    } else {
                      handleSignUpModal()
                    }
                  }}
                  name="Open blog pane"
                  style={{
                    margin: '30px 20px 0px',
                    padding: '7.5px 0px',
                    fontWeight: '500',
                    background: isAuth() ? COLORS.gold : COLORS.semiLightGray
                  }}
                >
                  Blog
                </StyledButtonContainer>

                <WalletAddress
                  walletAddress={profileItem?.wallet_address}
                  isAuth={isAuth}
                  handleSignUpModal={handleSignUpModal}
                />

                <Box
                  id="bottomBox"
                  position="static"
                  bottom="0"
                />
              </StyledModalContainer>
            </Box>
          )}
        </Popup>
      </Stack>
    </>
  )
}
